import { useApiQuery } from "@/hooks/use-api";
import {
  fetchSsoDomains,
  type ApiErrorInfo,
  type ApiSsoDomainChallenge,
  type SsoDomainListResult,
} from "@/lib/api";

/**
 * List hook for the SSO domain challenges, following the repo's documented
 * data-flow (AGENTS.md → "Data Flow: API → Hook → Component"): the page never
 * hand-rolls loading/error state, and `fetchSsoDomains` is the only source of
 * rows — there is no mock fallback, so an unreachable API produces an explicit
 * unavailable/error state rather than a seeded list.
 *
 * `enabled` is the permission gate: the request is not issued at all until the
 * loaded effective-access contract carries the SSO permission.
 */
const EMPTY_DOMAINS: ApiSsoDomainChallenge[] = [];

function mapSsoDomainResponse(raw: SsoDomainListResult) {
  return {
    data: raw.domains,
    error: raw.error,
    isEmpty: raw.domains.length === 0,
  };
}

/**
 * The domain read is owned by the page (`sso-admin.tsx`) and handed to the
 * cards that need it — the domains card, the provider form's verified-domain
 * picker and the enable dialog — so one page render issues one GET
 * /v1/admin/sso/domains rather than one per card.
 */
export interface SsoDomainsState {
  domains: ApiSsoDomainChallenge[];
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

export function useSsoDomains(enabled: boolean): SsoDomainsState {
  const result = useApiQuery(fetchSsoDomains, mapSsoDomainResponse, {
    initialData: EMPTY_DOMAINS,
    enabled,
  });

  return {
    domains: result.data,
    loading: result.loading,
    error: result.error,
    refresh: result.refresh,
  };
}

/** Only a VERIFIED challenge may be routed to a provider: the API refuses
 * anything else with SSO_DOMAIN_NOT_VERIFIED, so the picker must not offer it. */
export function verifiedDomains(state: SsoDomainsState): string[] {
  return state.domains.filter((domain) => domain.status === "VERIFIED").map((domain) => domain.domain);
}
